<!-- Page Header -->
<app-title-bar title="Orders" description="Track and manage your orders" [showButton]="false">
</app-title-bar>

<!-- Stats Cards -->
<div class="stats-container">
  <app-stats-card
    *ngFor="let stat of orderStats; let i = index"
    [title]="stat.title"
    [value]="stat.value"
    [color]="stat.color"
    [icon]="stat.icon"
    [iconClass]="stat.iconClass"
    [iconStyle]="stat.iconStyle"
    [style.animation-delay]="i * 0.1 + 's'"
  >
  </app-stats-card>
</div>

<!-- Search and Filter Section -->
<div class="search-filter-section">
  <app-searchbar
    placeholder="Search for orders by order ID, customer name, email..."
    [(searchTerm)]="searchTerm"
    (searchChange)="onSearchChange($event)"
    iconSize="small"
  >
  </app-searchbar>

  <div class="filter-container">
    <!-- Location Dropdown -->
    <app-simple-dropdown
      [options]="locationOptions"
      [selectedValue]="selectedLocationId"
      [title]="'Select Location'"
      [isOpen]="isLocationDropdownOpen"
      (selectionChange)="selectLocation($event)"
      (toggle)="toggleLocationDropdown()"
      (close)="closeLocationDropdown()"
    >
    </app-simple-dropdown>

    <app-simple-dropdown
      [options]="laneFilterOptions"
      *ngIf="showLaneFilter"
      [selectedValue]="selectedLaneFilterId"
      [placeholder]="'All Lanes'"
      [title]="'Select Lane'"
      [isOpen]="isLaneDropdownOpen"
      (selectionChange)="selectLaneFilter($event)"
      (toggle)="toggleLaneDropdown()"
      (close)="closeLaneDropdown()"
    >
    </app-simple-dropdown>

    <app-action-button
      buttonText="Filter"
      iconSrc="assets/images/package/adjustments.svg"
      [showCount]="hasActiveFilters"
      [count]="getActiveFilterCount()"
      (buttonClick)="onFilter()"
    >
    </app-action-button>

    <div class="csv-export-wrapper">
      <app-action-button
        [buttonText]="isExportingCsv ? 'Preparing CSV...' : 'Download CSV'"
        [disabled]="isExportingCsv"
        ariaLabel="Download filtered orders as CSV"
        (buttonClick)="toggleCsvDateRangePicker()"
      >
      </app-action-button>

      <app-date-range-picker
        [isOpen]="isCsvDateRangePickerOpen"
        [selectedPeriod]="csvSelectedPeriod"
        [customDateRange]="csvDateRange"
        [periodOptions]="csvExportPeriodOptions"
        [allowReapply]="true"
        applyButtonLabel="Download CSV"
        (close)="closeCsvDateRangePicker()"
        (apply)="onCsvDateRangeApply($event)"
      >
      </app-date-range-picker>
    </div>
  </div>
</div>

<!-- Orders Table (Only show when there are orders to display) -->
<div *ngIf="!loading && orders.length > 0" class="table-container">
  <app-orders-list-table
    [orders]="paginatedOrders"
    [getSortIcon]="getSortIcon.bind(this)"
    [getStatusClass]="getStatusClass.bind(this)"
    [getWashTypeClass]="getWashTypeClass.bind(this)"
    [formatPaymentMethod]="formatPaymentMethod.bind(this)"
    [getCapitalizedSource]="getCapitalizedSource.bind(this)"
    [canShowActions]="canShowActions.bind(this)"
    [canOpenDiscount]="true"
    (sortByOrderId)="sortByOrderId()"
    (sortByDateTime)="sortByDateTime()"
    (openOrderDetails)="openOrderDetails($event)"
    (toggleDropdown)="toggleDropdown($event.event, $event.orderId)"
  >
  </app-orders-list-table>
</div>

<div *ngIf="loading" class="loading-container">
  <app-loading-table-shimmer
    [columns]="shimmerColumns"
    [rows]="10"
    [showHeader]="false"
    className="scrollable"
  >
  </app-loading-table-shimmer>
</div>

<!-- Empty State - No Orders (when there are truly no orders) -->
<div *ngIf="!loading && orders.length === 0 && !hasSearchOrFilters" class="empty-state-container">
  <app-empty-result
    image="/assets/images/empty_screen.png"
    title="No orders to show"
    description="Start adding orders to track all order details here."
    [actionText]="'Add Order'"
    [showAction]="true"
    [actionClick]="onAddOrderClick"
  >
  </app-empty-result>
</div>

<!-- Empty State - No Search Results (when search/filters are active but no results) -->
<div
  *ngIf="
    !loading &&
    (orders.length === 0 || (orders.length > 0 && filteredOrders.length === 0)) &&
    hasSearchOrFilters
  "
  class="empty-state-container"
>
  <app-empty-result
    image="/assets/images/empty_screen.png"
    title="No results found!"
    description="Please try again or modify your search or filters."
    [actionText]="''"
    [showAction]="false"
  >
  </app-empty-result>
</div>

<!-- Pagination -->
<app-pagination-bar
  *ngIf="!loading && orders.length > 0 && filteredOrders.length > 0"
  [paginationData]="paginationData"
  [itemsPerPageOptions]="[10, 25, 50]"
  (pageChange)="onPageChange($event)"
  (itemsPerPageChange)="onItemsPerPageChange($event)"
>
</app-pagination-bar>

<!-- Filter Modal -->
<app-filter-modal
  [isVisible]="showFilterModal"
  header="Filters"
  primaryButtonText="Apply Filters"
  secondaryButtonText="Clear Filters"
  [categories]="filterCategories()"
  (close)="onCloseFilterModal()"
  (apply)="onFilterModalApplyShared($event)"
  (optionChange)="onFilterOptionChange($event)"
>
</app-filter-modal>

<!-- Order Details Modal -->
<app-order-details-modal
  [order]="selectedOrder"
  [isOpen]="showOrderDetailsModal"
  (closeModal)="closeOrderDetails()"
  (orderCancelled)="onOrderCancelled($event)"
  (bookAgain)="onBookAgainFromOrderDetails()"
></app-order-details-modal>

<!-- Add Order Modal - Only for Admin Users -->
<app-add-order-modal
  [isOpen]="showAddOrderModal"
  (close)="onCloseAddOrder()"
  (continue)="onContinueAddOrder($event)"
  (orderCreated)="onOrderCreated()"
  (viewOrderDetails)="onViewOrderDetails($event)"
></app-add-order-modal>

<!-- Cancel Order Modal -->
<app-cancel-order-modal
  [order]="orderToCancel"
  [isOpen]="showCancelOrderModal"
  (close)="closeCancelOrderModal()"
  (confirm)="onCancelOrderConfirm($event)"
></app-cancel-order-modal>

<!-- CWS-1779: cancel & refund panel (CWS-1778), opened from the row menu -->
<app-cancel-refund-panel
  [isOpen]="showCancelRefundPanel"
  [orderId]="cancelRefundOrder?.id ?? null"
  [locationId]="cancelRefundLocationId"
  [intent]="cancelRefundIntent"
  [orderNumber]="cancelRefundOrder?.orderId ?? null"
  [customerName]="cancelRefundOrder?.customerName ?? null"
  [orderDate]="cancelRefundOrder?.dateTime ?? null"
  (closed)="onCancelRefundClosed()"
  (done)="onCancelRefundDone($event)"
></app-cancel-refund-panel>

<!-- Cancellation Success Modal -->
<app-order-cancellation-success-modal
  [isOpen]="showCancellationSuccessModal"
  [refundAmount]="cancellationRefundAmount"
  [isScheduledOrder]="cancellationWasScheduled"
  (close)="onCancellationSuccessClose()"
  (bookAgain)="onCancellationBookAgain()"
></app-order-cancellation-success-modal>

<!-- Global Dropdown Menu -->
<app-orders-actions-dropdown
  [openDropdownId]="openDropdownId"
  [selectedOrder]="selectedOrderForDropdown"
  [alwaysShowCancel]="true"
  [canCancelOrder]="canCancelOrder.bind(this)"
  (view)="viewOrder($event)"
  (cancel)="cancelOrder($event)"
  (refund)="refundOrder($event)"
  (retryRefund)="retryRefund($event)"
>
</app-orders-actions-dropdown>
